import React from 'react'
import { useDropzone } from 'react-dropzone'
import { IconCloudUpload, IconPlus } from '@tabler/icons-react'
import { isEmpty, unionBy } from 'lodash'
import classNames from 'classnames'

import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/toasts/toast.helpers'

import { FilePreview } from './FilePreview'

import './Dropzone.scss'

export const DragAndDropFileUploader = ({
  name,
  label,
  maxSize = 1048576,
  acceptedFileTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/svg+xml': ['.svg'],
  },
  acceptedFileMessage = 'JPG, PNG, SVG. Max file size: 1MB',
  dark = false,
  existingValue,
  imageCache,
  maxFiles = 1,
  multiple = false,
  useIconLayout = true,
  allowEdits = true,
  cardVariant = false,
  emptyStateTitle = 'Drag your files here or click to upload',
  emptyStateButtonText = 'Upload Files',
  ...formikProps
}) => {
  const value = formikProps.values[name]
  const existingUrlWithCache = `${existingValue}?${imageCache}`
  const hasFiles = !isEmpty(value)

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: acceptedFileTypes,
    maxFiles,
    multiple,
    maxSize,
    disabled: !allowEdits,
    onDropAccepted: (acceptedFiles) => {
      if (!multiple || formikProps.values[name] == null) {
        formikProps.setFieldValue(name, acceptedFiles)
      } else {
        const newFields = unionBy(formikProps.values[name], acceptedFiles, 'name')
        formikProps.setFieldValue(name, newFields)
      }
      formikProps.setSubmitting(false)
    },
    onDropRejected: (err) => {
      const errorMessage = err[0]?.errors[0]?.message

      toast.error(errorMessage || 'File not accepted')
    },
    onError: () => {
      toast.error('Something went wrong')
    },
  })

  if (cardVariant && hasFiles) {
    // Queued/uploading/complete state: do NOT apply getRootProps to the outer
    // wrapper — it would intercept clicks on the × remove button. Only the
    // file input and the "+ Add New File" trigger the picker. Drag-and-drop
    // is intentionally off in this state.
    const handleAddNewKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        open()
      }
    }

    return (
      <FormField>
        {label && <label>{label}</label>}
        <div
          data-testid={`${name}-file-uploader`}
          className={classNames('dropzone', 'dropzone--card', 'dropzone--has-files')}
        >
          <input {...getInputProps()} type="file" />
          <div className="dropzone-container dropzone-container--card">
            {allowEdits && (
              <div
                className="file-uploader-add-new"
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={handleAddNewKeyDown}
              >
                <IconPlus className="icon-svg" />
                <span>Add New File</span>
              </div>
            )}
            <div className="file-list-scroll">
              {value.map((file) => (
                <FilePreview
                  key={file.name}
                  name={name}
                  existingValue={existingValue}
                  file={file}
                  formikProps={formikProps}
                  dark={dark}
                  useIconLayout={useIconLayout}
                  allowEdits={allowEdits}
                  rowVariant="card"
                />
              ))}
            </div>
          </div>
        </div>
      </FormField>
    )
  }

  if (cardVariant) {
    // Empty state: whole container is clickable + draggable via getRootProps.
    return (
      <FormField>
        {label && <label>{label}</label>}
        <div
          data-testid={`${name}-file-uploader`}
          {...getRootProps({
            className: classNames('dropzone', 'dropzone--card'),
          })}
        >
          <input {...getInputProps()} type="file" />
          {isDragActive ? (
            <div className="dropzone-container is-drag-active">
              <h2>Drop file here...</h2>
            </div>
          ) : (
            <div className="dropzone-container dropzone-container--card-empty">
              <div className="dropzone-container__content">
                <IconCloudUpload className="drop-icon" size={42} strokeWidth={1.5} />
                <div className="dropzone-container__text">
                  <h2 className="dropzone-container__title">{emptyStateTitle}</h2>
                  {acceptedFileMessage && (
                    <p className="dropzone-container__message">
                      <small>{acceptedFileMessage}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="balto-button brand">{emptyStateButtonText}</div>
            </div>
          )}
        </div>
      </FormField>
    )
  }

  return (
    <FormField>
      <label>{label}</label>
      <div data-testid={`${name}-file-uploader`} {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} type="file" />
        {isDragActive ? (
          <div className="dropzone-container is-drag-active">
            <h2>Drop file here...</h2>
          </div>
        ) : (
          <div className="dropzone-container">
            {!useIconLayout && !isEmpty(formikProps.values[name]) && (
              <div className="full-width flex-align-center flex-space-between">
                <h3>
                  {allowEdits
                    ? `Files ready for upload (${formikProps.values[name].length})`
                    : 'Uploading files...'}
                </h3>
                <div className={classNames('balto-button brand', { disabled: !allowEdits })}>
                  Add New File
                </div>
              </div>
            )}
            {!isEmpty(value) ? (
              value.map((file) => {
                return (
                  <div key={file.name} className="full-width">
                    <FilePreview
                      name={name}
                      existingValue={existingValue}
                      file={file}
                      formikProps={formikProps}
                      dark={dark}
                      useIconLayout={useIconLayout}
                      allowEdits={allowEdits}
                    />
                  </div>
                )
              })
            ) : existingValue ? (
              <img src={existingUrlWithCache} alt={name} className="preview-image" />
            ) : (
              <>
                <IconCloudUpload className="drop-icon" />
                <h2>Drag and Drop</h2>
                <p>Select a file or drop one here.</p>
              </>
            )}
            {isEmpty(formikProps.values[name]) && (
              <>
                <p className="text-center mb">
                  <small>{acceptedFileMessage}</small>
                </p>
                <div className="balto-button brand">Select Files</div>
              </>
            )}
          </div>
        )}
      </div>
    </FormField>
  )
}
